import { AxiosRequestConfig, Method } from "axios";
import { api } from "@/shared/api/client";
import { HttpContract, HttpService, QueryParams } from "@/shared/api/contracts";

import { isTauriRuntime } from "@/shared/lib/tauri";
type ExtendedRequestConfig = AxiosRequestConfig & {
    skipAuth?: boolean;
};

type RequestOptions<
    TBody,
    TQuery extends QueryParams | undefined,
> = {
    body?: TBody;
    query?: TQuery;
    config?: Omit<ExtendedRequestConfig, "url" | "method" | "data" | "params">;
};

const SERVICE_BASE_PATHS: Record<HttpService, string> = {
    core: "",
    notification: "",
    crm: "/chat",
};

function normalizePath(path: string) {
    if (!path) {
        return "/";
    }

    return path.startsWith("/") ? path : `/${path}`;
}

function buildUrl(service: HttpService, path: string) {
    const basePath = SERVICE_BASE_PATHS[service];
    return `${basePath}${normalizePath(path)}`;
}

const isTauri = isTauriRuntime();

class AppHttpClient {
    async request<
        TResponse,
        TBody = undefined,
        TQuery extends QueryParams | undefined = undefined,
    >(
        contract: HttpContract<TResponse, TBody, TQuery>,
        options: RequestOptions<TBody, TQuery> = {}
    ): Promise<TResponse> {
        let customBaseUrl = options.config?.baseURL;
        
        if (contract.service === "crm") {
            customBaseUrl = isTauri && process.env.NODE_ENV === "production"
                ? (process.env.NEXT_PUBLIC_CRM_API_URL?.replace(/\/(api\/chat|api|chat)\/?$/, "") || "https://7days-gestaodeobras-be-crm-production.up.railway.app")
                : undefined;
        }

        const requestConfig: ExtendedRequestConfig = {
            ...options.config,
            ...(customBaseUrl !== undefined ? { baseURL: customBaseUrl } : {}),
            url: buildUrl(contract.service, contract.path),
            method: contract.method as Method,
            params: options.query,
            data: options.body,
            skipAuth: contract.authenticated === false,
        };

        const response = await api.request<TResponse>(requestConfig);
        return response.data;
    }
}

export const httpClient = new AppHttpClient();
